import {
  COMPARISON_MEASURE_COLUMN_NAME,
  COMPARISON_TIMESTAMP_COLUMN_NAME,
  defaultComparisonForGranularity,
  definitionWithComparison,
  getComparisonGranularity,
} from './features/comparison';
import { isTimelessWithWarehousingEnabled, MEASURE_COLUMN_NAME, TIMESTAMP_COLUMN_NAME } from './features/metricValue';
import { getFirstWindow, isLastNWindow } from './features/window/integration';
import { isDefaultLineStaticRefLine } from './features/threshold/getters';
import { DateGranularity, EffectiveDefinition } from './models';

const constantEffectiveDefinition: EffectiveDefinition = {
  schema: {
    measure: {
      type: 'number',
      name: MEASURE_COLUMN_NAME,
    },
    timestamp: {
      type: 'datetime',
      name: TIMESTAMP_COLUMN_NAME,
      dateGranularity: DateGranularity.DAY,
    },
    color: {
      type: 'string',
      name: 'color',
    },
    comparisonMeasure: {
      type: 'number',
      name: COMPARISON_MEASURE_COLUMN_NAME,
    },
    comparisonTimestamp: {
      type: 'datetime',
      name: COMPARISON_TIMESTAMP_COLUMN_NAME,
    },
  },
  features: {
    color: {
      column: 'color',
    },
    measure: { column: MEASURE_COLUMN_NAME },
    timestamp: { column: TIMESTAMP_COLUMN_NAME },
    threshold: {
      thresholds: [
        {
          lines: [
            {
              measure: { column: COMPARISON_MEASURE_COLUMN_NAME },
              timestamp: { column: COMPARISON_TIMESTAMP_COLUMN_NAME },
            },
          ],
        },
      ],
    },
  },
};

export const getConstantEffectiveDefinition = (): EffectiveDefinition => {
  return constantEffectiveDefinition;
};

export const testEffectiveDefinition = getConstantEffectiveDefinition();

const constantSingleNumberEffectiveDefinition: EffectiveDefinition = {
  schema: {
    measure: {
      type: 'number',
      name: MEASURE_COLUMN_NAME,
    },
    timestamp: {
      type: 'datetime',
      name: TIMESTAMP_COLUMN_NAME,
      dateGranularity: DateGranularity.DAY,
      isSnapshotTimestamp: true,
      isTimeless: true,
    },
    color: {
      type: 'string',
      name: 'color',
    },
    comparisonMeasure: {
      type: 'number',
      name: COMPARISON_MEASURE_COLUMN_NAME,
    },
    comparisonTimestamp: {
      type: 'datetime',
      name: COMPARISON_TIMESTAMP_COLUMN_NAME,
    },
  },
  features: {
    color: {
      column: 'color',
    },
    measure: { column: MEASURE_COLUMN_NAME },
    timestamp: { column: TIMESTAMP_COLUMN_NAME },
    threshold: {
      thresholds: [
        {
          lines: [
            {
              measure: { column: COMPARISON_MEASURE_COLUMN_NAME },
              timestamp: { column: COMPARISON_TIMESTAMP_COLUMN_NAME },
            },
          ],
        },
      ],
    },
  },
};

export const getConstantSingleNumberEffectiveDefinition = (): EffectiveDefinition => {
  return constantSingleNumberEffectiveDefinition;
};

export const testSingleNumberEffectiveDefinition = getConstantSingleNumberEffectiveDefinition();

/**
 * Defect 1450480. This function takes a definition and makes sure that the window and comparison are of day
 * granularity if the metric is warehoused
 *
 * @param definition the EffectiveDefinition
 * @returns The same definition with an altered window and/or comparison if the metric is warehoused and has
 * a mark based window and/or a mark based comparison
 */
export const getWarehousedDefinitionWithDayConfig = (definition: EffectiveDefinition): EffectiveDefinition => {
  if (isTimelessWithWarehousingEnabled(definition)) {
    const window = getFirstWindow(definition);
    if (window && isLastNWindow(window)) {
      // Since an unedited warehoused metric would have no date window, we want to remove the 100 Mark date window
      // instead of replacing it with a 90 Day window.
      if (definition.features.dateWindow?.dateWindows?.length === 1) {
        const features = { ...definition.features };
        delete features.dateWindow;
        definition = {
          ...definition,
          features,
        };
      } else {
        definition = {
          ...definition,
          features: {
            ...definition.features,
            dateWindow: {
              dateWindows: definition.features.dateWindow?.dateWindows?.slice(1),
            },
          },
        };
      }
    }

    const isMarkBasedComparison =
      !isDefaultLineStaticRefLine(definition) && getComparisonGranularity(definition) === undefined;
    if (isMarkBasedComparison) {
      definition = definitionWithComparison(definition, defaultComparisonForGranularity(DateGranularity.DAY), true);
    }
  }

  return definition;
};
